import { useId, useState } from 'react';

import Button from '@/components/Button/Button';
import type { Home } from '@/features/homeAutomation/home';
import useCurrentHome from '@/features/homeAutomation/hooks/useCurrentHome';
import useUpdateHome, { HOME_NAME_MAX, HOME_NAME_MIN, isValidHomeName } from '@/features/homeAutomation/hooks/useUpdateHome';

import { formatTimestamp } from '../lib/format';
import AsyncSection from './AsyncSection';
import ReadOnlyRow from './ReadOnlyRow';

interface HomeFormProps {
  home: Home;
}

function HomeForm({ home }: HomeFormProps) {
  const [name, setName] = useState(home.name);
  const update = useUpdateHome();
  const inputId = useId();
  const helperId = useId();

  const trimmed = name.trim();
  const isDirty = trimmed !== home.name;
  const isValid = isValidHomeName(name);
  const canSave = isDirty && isValid && !update.isPending;

  const helper = !isValid ? `Name must be ${HOME_NAME_MIN}–${HOME_NAME_MAX} characters.` : !isDirty ? 'No changes to save.' : '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        update.mutate({ id: home.id, name });
      }}
      className="space-y-4"
    >
      <div className="space-y-1.5">
        <label htmlFor={inputId} className="text-xs font-medium text-(--text-secondary)">
          Home name
        </label>
        <input
          id={inputId}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={HOME_NAME_MAX}
          aria-describedby={helper ? helperId : undefined}
          aria-invalid={!isValid}
          className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
        />
        {helper && (
          <p id={helperId} className="text-xs text-(--text-tertiary)">
            {helper}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" size="sm" loading={update.isPending} disabled={!canSave}>
          Save
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={!isDirty || update.isPending} onClick={() => setName(home.name)}>
          Reset
        </Button>
      </div>

      <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) px-3 py-2">
        <ReadOnlyRow label="Home id" value={home.id} mono copyLabel="home id" />
        <ReadOnlyRow label="Owner uid" value={home.ownerUid} mono />
        <ReadOnlyRow label="Created" value={formatTimestamp(home.createdAt)} />
        <ReadOnlyRow label="Updated" value={formatTimestamp(home.updatedAt)} />
      </div>
    </form>
  );
}

export default function HomesSection() {
  const query = useCurrentHome();

  return (
    <AsyncSection
      loading={query.isPending}
      error={query.isError || !query.data}
      errorMessage="Failed to load home."
      onRetry={() => void query.refetch()}
    >
      {query.data && <HomeForm home={query.data} />}
    </AsyncSection>
  );
}
